import {Injectable} from '@angular/core';
import {GATEWAYS_AGGREGATED_STATUS} from '@constants/machine/gateways-aggregated-status';
import {Gateway} from '@models/gateway/gateway';
import {CONNECTION_STATE} from '@models/gateway/connection/connection-state.constant';

import {TRAFFIC_LIGHT_STATUS, TRAFFIC_LIGHT_STATUS_CLASS} from '@constants/machine/traffic-light/traffic-light-status';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {GATEWAY_STATUS} from '@constants/gateway/gateway-status';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {GatewayStatusService} from '@services/gateway-status/gateway-status.service';
import {DashboardConfiguration} from '@models/dashboard/dashboard-configuration';
import {MachineIdList} from '@models/dashboard/machine-list/machine-id-list';

@Injectable({
  providedIn: 'root'
})
export class MachineDetailsService {
  private LIST_DASHBOARD_CONFIGURATION_URL_SUFFIX: string = '/api/web/admin/machine/listDashboardConfigurations';
  private LIST_SIGNALS_URL_SUFFIX: string = '/api/web/admin/machine/:id/getSignals';
  private SAVE_CONFIGURATION_URL_SUFFIX: string = '/api/web/admin/machine/:id/saveConfig';
  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocationService: ApiLocationService,
              private gatewayStatusService: GatewayStatusService) {
    this.BASE_URL = this.apiLocationService.getLocation();
  }

  public determineAggregatesGatewaysStatus(gatewayStatusData: Gateway[], missingConfiguration: boolean): GATEWAYS_AGGREGATED_STATUS {
    let connectedCP = 0;
    let notSendingDataCP = 0;
    if (!gatewayStatusData || gatewayStatusData.length === 0) {
      return GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS;
    }

    if (missingConfiguration) {
      return GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION;
    }

    for (const gatewayStatusDataItem of gatewayStatusData) {
      if (gatewayStatusDataItem.connectionStatus.state === CONNECTION_STATE.ONLINE) {
        connectedCP++;
        if (this.gatewayStatusService.determineGatewayStatusByType(gatewayStatusDataItem) !== GATEWAY_STATUS.ONLINE) {
          notSendingDataCP++;
        }
      }
    }
    let status: GATEWAYS_AGGREGATED_STATUS;
    if ((connectedCP === 0 && gatewayStatusData.length > 0) || gatewayStatusData.length === 0) {
      status = GATEWAYS_AGGREGATED_STATUS.DISCONNECTED;
    } else {
      if (connectedCP === gatewayStatusData.length && gatewayStatusData.length > 0) {
        if (notSendingDataCP === 0) {
          status = GATEWAYS_AGGREGATED_STATUS.CONNECTED;
        } else {
          status = GATEWAYS_AGGREGATED_STATUS.PARTIALLY_DISCONNECTED;
        }
      } else {
        status = GATEWAYS_AGGREGATED_STATUS.PARTIALLY_DISCONNECTED;
      }
    }


    return status;
  }

  public determineGatewayConnectorStatus(gateways: Gateway[]): boolean {
    return !gateways?.some(gateway =>
      gateway.connectionStatus.state === CONNECTION_STATE.ONLINE && !gateway.connectorsActivated
    );
  }

  public generateGatewaysStatusClass(status: GATEWAYS_AGGREGATED_STATUS, gateways: Gateway[], isForTiles: boolean): string {
    let resultClass: string = 'dark';
    if (status === GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS || status === GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION) {
      resultClass = 'info';
    } else {
      if (gateways) {
        switch (status) {
          case GATEWAYS_AGGREGATED_STATUS.PARTIALLY_DISCONNECTED:
            resultClass = 'warning';
            break;
          case GATEWAYS_AGGREGATED_STATUS.CONNECTED:
            resultClass = 'success';
            break;
          case GATEWAYS_AGGREGATED_STATUS.DISCONNECTED:
            resultClass = 'danger';
            break;
        }
      }
    }
    if (isForTiles) {
      resultClass = 'btn-outline-' + resultClass;
    } else {
      resultClass = 'badge badge-' + resultClass;
    }
    return resultClass;
  }

  public determineTileTrafficLightClass(backendResponse: any,
                                        gatewaysStatus: GATEWAYS_AGGREGATED_STATUS) {

    if (backendResponse?.cloudplugs &&
        [GATEWAYS_AGGREGATED_STATUS.DISCONNECTED,
          GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS,
          GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION].indexOf(gatewaysStatus) > -1) {
      return TRAFFIC_LIGHT_STATUS_CLASS.OFFLINE;
    }

    if (!backendResponse?.trafficLight || !backendResponse.trafficLight.status) {
      return TRAFFIC_LIGHT_STATUS_CLASS.OFFLINE;
    }

    let result: string = '';
    switch (backendResponse.trafficLight.status) {
      case TRAFFIC_LIGHT_STATUS.FAILURE:
        result = TRAFFIC_LIGHT_STATUS_CLASS.FAILURE;
        break;

      case TRAFFIC_LIGHT_STATUS.WARNING:
        result = TRAFFIC_LIGHT_STATUS_CLASS.WARNING;
        break;

      case TRAFFIC_LIGHT_STATUS.RUNNING:
        result = TRAFFIC_LIGHT_STATUS_CLASS.RUNNING;
        break;

      case TRAFFIC_LIGHT_STATUS.INFO:
        result = TRAFFIC_LIGHT_STATUS_CLASS.INFO;
        break;

      case TRAFFIC_LIGHT_STATUS.CANNOT_EVALUATE:
        result = TRAFFIC_LIGHT_STATUS_CLASS.CANNOT_EVALUATE;
        break;

    }

    return result;
  }

  public determineListTrafficLightClass(backendResponse: any,
                                        gatewaysStatus: GATEWAYS_AGGREGATED_STATUS,
                                        gatewaysConnectorsActive: boolean): string {

    const fallbackValue = 'badge badge-dark';

    if (backendResponse.cloudplugs &&
        [GATEWAYS_AGGREGATED_STATUS.DISCONNECTED,
          GATEWAYS_AGGREGATED_STATUS.NO_GATEWAYS,
          GATEWAYS_AGGREGATED_STATUS.MISCONFIGURATION].indexOf(gatewaysStatus) > -1) {
      return fallbackValue;
    }

    if (!gatewaysConnectorsActive) {
      return fallbackValue;
    }

    if (!backendResponse.trafficLight || !backendResponse.trafficLight.status) {
      return fallbackValue;
    }

    let result: string = fallbackValue;
    switch (backendResponse.trafficLight.status) {
      case TRAFFIC_LIGHT_STATUS.FAILURE:
        result = 'badge badge-danger';
        break;

      case TRAFFIC_LIGHT_STATUS.WARNING:
        result = 'badge badge-warning';
        break;

      case TRAFFIC_LIGHT_STATUS.RUNNING:
        result = 'badge badge-success';
        break;

      case TRAFFIC_LIGHT_STATUS.INFO:
        result = 'badge badge-info';
        break;

      case TRAFFIC_LIGHT_STATUS.CANNOT_EVALUATE:
        result = 'badge badge-dark';
        break;

    }

    return result;
  }


  public getMachineDisplayProperties(machine: DashboardMachine | undefined): any {
    if (machine === null || machine === undefined) {
      return;
    }
    return {
      machineName: machine.type,
      machineType: machine.category,
      machineIconPath: './assets/img/machine_type/' + machine.category + '.png'
    };
  }

  public listAllDashboardConfigurations(machineIdList: MachineIdList): Observable<any> {
    const url: string = this.BASE_URL + this.LIST_DASHBOARD_CONFIGURATION_URL_SUFFIX;
    return this.http.post(url, machineIdList, HTTPS_REQUEST_OPTIONS);
  }

  public getSignals(machineId: number): Observable<any> {
    let url: string = this.BASE_URL + this.LIST_SIGNALS_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveConfiguration(machineId: number, configuration: DashboardConfiguration): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.post(url, configuration, HTTPS_REQUEST_OPTIONS);
  }
}
